import type {Component, Editor} from 'grapesjs';
import {getComponentStyleProperty} from '@EmailMakerReact/lib/email-maker-component-style-library';
import {updateCellsBorderRadius, updateCellsBorderWidth, updateCellsSpacing} from '@EmailMakerReact/lib/email-maker-table-style-library';
import {updateSocialLinkSize, updateSocialLinkSpacing} from '@EmailMakerReact/lib/email-maker-social-signup-library';
import {refreshSelectedCountdown} from '@EmailMakerReact/lib/email-maker-countdown-library';
import {RANGE_FILL_COLOR, RANGE_TRACK_COLOR, SLIDER_CSS_PROPS} from './slider-control.types';
import type {SliderControlDescriptor} from './slider-control.types';

export function parseAndClamp(raw: string, min: number, max: number): number {
  const parsed = parseInt(raw, 10);
  if (isNaN(parsed)) {
    return min;
  }
  return Math.min(max, Math.max(min, parsed));
}

export function parseRawCssToNumeric(raw: string, descriptor: SliderControlDescriptor): number {
  const parsed = parseInt(raw, 10);
  if (!isNaN(parsed)) {
    return parsed;
  }
  const def = descriptor.defaults;
  if (def !== undefined) {
    const defParsed = typeof def === 'number' ? def : parseInt(def, 10);
    if (!isNaN(defParsed)) {
      return defParsed;
    }
  }
  return descriptor.min;
}

export function getExtendedPrefixes(property: string): {top: string; bottom: string; left: string; right: string} {
  if (property === SLIDER_CSS_PROPS.BORDER_RADIUS) {
    return {
      top: 'border-top-right-radius',
      bottom: 'border-bottom-right-radius',
      left: 'border-bottom-left-radius',
      right: 'border-top-left-radius',
    };
  }
  return {
    top: `${property}-top`,
    bottom: `${property}-bottom`,
    left: `${property}-left`,
    right: `${property}-right`,
  };
}

export function resolveInitialValue(component: Component, descriptor: SliderControlDescriptor): number {
  const raw = getComponentStyleProperty(component, descriptor.property);
  return parseRawCssToNumeric(raw ?? '', descriptor);
}

export function resolveMax(component: Component, descriptor: SliderControlDescriptor): number {
  if (descriptor.property !== SLIDER_CSS_PROPS.BORDER_RADIUS) {
    return descriptor.max;
  }
  const el = component.getEl();
  if (el) {
    const rect = el.getBoundingClientRect();
    const dynamicMax = Math.floor(Math.min(rect.width, rect.height) / 2);
    if (dynamicMax > 0) {
      return dynamicMax;
    }
  }
  return descriptor.max;
}

export function buildRangeFillStyle(value: number, min: number, max: number): {background: string} {
  const range = max - min;
  const percentage = range > 0 ? ((value - min) / range) * 100 : 0;
  const clamped = Math.min(100, Math.max(0, percentage));
  return {
    background: `linear-gradient(90deg, ${RANGE_FILL_COLOR} ${clamped}%, ${RANGE_TRACK_COLOR} ${clamped}%)`,
  };
}

export function dispatchCustomProperty(editor: Editor, property: string, numericValue: number): void {
  if (property === SLIDER_CSS_PROPS.PMW_CELL_SPACING) {
    updateCellsSpacing(editor, numericValue);
  } else if (property === SLIDER_CSS_PROPS.PMW_CELL_BORDER_WIDTH) {
    updateCellsBorderWidth(editor, numericValue);
  } else if (property === SLIDER_CSS_PROPS.PMW_CELL_BORDER_RADIUS) {
    updateCellsBorderRadius(editor, numericValue);
  } else if (property === SLIDER_CSS_PROPS.PMW_ICON_MARGIN) {
    updateSocialLinkSpacing(editor, numericValue);
  } else if (property === SLIDER_CSS_PROPS.PMW_ICON_SIZE) {
    updateSocialLinkSize(editor, numericValue);
  } else if (property === SLIDER_CSS_PROPS.PMW_COUNTDOWN_LABEL_SIZE || property === SLIDER_CSS_PROPS.WIDTH) {
    refreshSelectedCountdown(editor);
  }
}

// Per-component attribute, must be re-read whenever the selected component changes.
export function readExtendedOpen(component: Component): boolean {
  const attrs = component.getAttributes() as Record<string, unknown>;
  return attrs['data-has-extended-controls'] === '1' || attrs['data-has-extended-controls'] === 1;
}
